import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmbeddingSettings,
  IngestionPipelineOutput,
  IngestionResultStatus,
  IngestionStateModel,
  IngestionStateStatus,
  LLMSettings,
  NodeParsingMode,
  ProviderModel,
  ProviderModelType,
} from '../../../gamma-api';
import IbTypography from '../common/IbTypography';
import ModelSelector from '../ModelSelector';
import IbButton, { IIbButtonAction } from '../common/IbButton';
import { getPercent, toNumberOrUndefined } from '../../utils/numberUtil';
import Switcher from '../common/Switcher';
import { getDefaultIfUndefined } from '../../utils/typeUtil';
import {
  DEFAULT_CHUNK_OVERLAP,
  DEFAULT_CHUNK_SIZE,
  DEFAULT_LLM_SPLITTER_PROMPT,
  DEFAULT_SENTENCE_COUNT,
  DEFAULT_SENTENCE_MAX_LENGTH,
  DEFAULT_SENTENCE_MIN_LENGTH,
  DEFAULT_WINDOW_SENTENCE_PADDING,
  MIN_CHUNK_OVERLAP,
  MIN_CHUNK_SIZE,
  MIN_DOCUMENT_MAX_SIZE,
  MIN_SENTENCE_COUNT,
  MIN_SENTENCE_MAX_LENGTH,
  MIN_SENTENCE_MIN_LENGTH,
  MIN_WINDOW_SENTENCE_PADDING,
} from '../../constants/projectPipeline';
import PromptEditorExpandable from '../common/PromptEditorExpandable';
import IbScrollSpy from '../common/IbScrollSpy';
import IbNumberInput from '../common/IbNumberInput';
import { PromptLanguage } from '../common/PromptEditor/const';
import { CheckFieldValueFnMap, useValidationForm } from '../../utils/validationUtil';
import { buildModelSelectorOptions, buildModelSelectorValue } from '../ModelSelector/utils';
import IbIcon from '../common/IbIcon';

import {
  MAIN_CLASS_NAME,
  CONTENT_CLASS_NAME,
  BLOCK_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  BLOCK_TITLE_CLASS_NAME,
  FOOTER_CLASS_NAME,
} from './const';

type IngestionPipelineSettingsForm = {
  embedding: EmbeddingSettings | undefined;
};

export interface IProjectIngestionPipelineProps {
  ingestionPipeline?: IngestionPipelineOutput;
  providers: ProviderModel[];
  providersLoading: boolean;
  ingestionState?: IngestionStateModel;
  onChange?: (ingestionPipeline: IngestionPipelineOutput) => void;
  onSave?: () => Promise<void>;
  onRebuild?: () => Promise<void>;
}

const ProjectIngestionPipeline: React.FC<IProjectIngestionPipelineProps> = ({
  ingestionPipeline,
  providers,
  providersLoading,
  ingestionState,
  onSave,
  onChange,
  onRebuild,
}) => {
  const { t } = useTranslation();

  const checkFieldValueFnMap: CheckFieldValueFnMap<IngestionPipelineSettingsForm> = {
    embedding: (value: EmbeddingSettings | undefined) => {
      if (!value?.model_name) return false;

      const modelValue = buildModelSelectorValue(value?.provider_id, value?.model_name);
      const options = buildModelSelectorOptions(providers, ProviderModelType.Embedding);
      return options.some((o) => o.value === modelValue);
    },
  };

  const initialValues: IngestionPipelineSettingsForm = {
    embedding: ingestionPipeline?.embedding,
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    validate,
    updateFormValues,
    getShowError,
    getErrorMessage,
  } = useValidationForm<IngestionPipelineSettingsForm>(initialValues, checkFieldValueFnMap);

  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  const [chunkSizeShowDefault, setChunkSizeShowDefault] = useState(true);
  const [chunkOverlapShowDefault, setChunkOverlapShowDefault] = useState(true);
  const [sentenceCountShowDefault, setSentenceCountShowDefault] = useState(true);
  const [windowSentencePaddingShowDefault, setWindowSentencePaddingShowDefault] = useState(true);
  const [sentenceMinLengthShowDefault, setSentenceMinLengthShowDefault] = useState(true);
  const [sentenceMaxLengthShowDefault, setSentenceMaxLengthShowDefault] = useState(true);
  const [llmNodeParsingChunkSizeShowDefault, setLLMNodeParsingChunkSizeShowDefault] = useState(true);

  const chunkSize = chunkSizeShowDefault
    ? getDefaultIfUndefined(ingestionPipeline?.chunk_node_parsing?.chunk_size, DEFAULT_CHUNK_SIZE)
    : ingestionPipeline?.chunk_node_parsing?.chunk_size;

  const chunkOverlap = chunkOverlapShowDefault
    ? getDefaultIfUndefined(ingestionPipeline?.chunk_node_parsing?.chunk_overlap, DEFAULT_CHUNK_OVERLAP)
    : ingestionPipeline?.chunk_node_parsing?.chunk_overlap;

  const sentenceCount = sentenceCountShowDefault
    ? getDefaultIfUndefined(ingestionPipeline?.sentence_window_node_parsing?.sentence_count, DEFAULT_SENTENCE_COUNT)
    : ingestionPipeline?.sentence_window_node_parsing?.sentence_count;

  const windowSentencePadding = windowSentencePaddingShowDefault
    ? getDefaultIfUndefined(
        ingestionPipeline?.sentence_window_node_parsing?.window_sentence_padding,
        DEFAULT_WINDOW_SENTENCE_PADDING
      )
    : ingestionPipeline?.sentence_window_node_parsing?.window_sentence_padding;

  const sentenceMinLength = sentenceMinLengthShowDefault
    ? getDefaultIfUndefined(
        ingestionPipeline?.sentence_window_node_parsing?.sentence_min_length,
        DEFAULT_SENTENCE_MIN_LENGTH
      )
    : ingestionPipeline?.sentence_window_node_parsing?.sentence_min_length;

  const sentenceMaxLength = sentenceMaxLengthShowDefault
    ? getDefaultIfUndefined(
        ingestionPipeline?.sentence_window_node_parsing?.sentence_max_length,
        DEFAULT_SENTENCE_MAX_LENGTH
      )
    : ingestionPipeline?.sentence_window_node_parsing?.sentence_max_length;

  const llmNodeParsingChunkSize = llmNodeParsingChunkSizeShowDefault
    ? getDefaultIfUndefined(ingestionPipeline?.llm_node_parsing?.chunk_size, DEFAULT_CHUNK_SIZE)
    : ingestionPipeline?.llm_node_parsing?.chunk_size;

  const rebuildDisabled =
    rebuilding ||
    !ingestionState ||
    ingestionState.status == IngestionStateStatus.Processing ||
    (ingestionState.result.status == IngestionResultStatus.Ready &&
      ingestionState.status == IngestionStateStatus.Pending);

  useEffect(() => {
    updateFormValues(initialValues);
  }, [ingestionPipeline]);

  const onSaveButtonClick = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    await onSave?.();
    setSaving(false);
  };

  const onRebuildButtonClick = async () => {
    if (!validate()) {
      return;
    }

    setRebuilding(true);
    await onRebuild?.();
    setRebuilding(false);
  };

  const onEmbeddingChange = (embedding: EmbeddingSettings) => {
    onChange?.({ ...ingestionPipeline, embedding } as IngestionPipelineOutput);
    onFieldChange('embedding', embedding);
  };

  const onEmbeddingBlur = () => {
    onFieldBlur('embedding');
  };

  const onNodeParsingModeChange = (value: string | boolean) => {
    onChange?.({ ...ingestionPipeline, node_parsing_mode: value } as IngestionPipelineOutput);
  };

  const onChunkSizeChange = (value?: number | string) => {
    onChange?.({
      ...ingestionPipeline,
      chunk_node_parsing: {
        ...ingestionPipeline?.chunk_node_parsing,
        chunk_size: toNumberOrUndefined(value),
      },
    } as IngestionPipelineOutput);
    setChunkSizeShowDefault(false);
  };

  const onChunkOverlapChange = (value?: number | string) => {
    onChange?.({
      ...ingestionPipeline,
      chunk_node_parsing: {
        ...ingestionPipeline?.chunk_node_parsing,
        chunk_overlap: toNumberOrUndefined(value),
      },
    } as IngestionPipelineOutput);
    setChunkOverlapShowDefault(false);
  };

  const onSentenceCountChange = (value?: number | string) => {
    onChange?.({
      ...ingestionPipeline,
      sentence_window_node_parsing: {
        ...ingestionPipeline?.sentence_window_node_parsing,
        sentence_count: toNumberOrUndefined(value),
      },
    } as IngestionPipelineOutput);
    setSentenceCountShowDefault(false);
  };

  const onWindowSentencePaddingChange = (value?: number | string) => {
    onChange?.({
      ...ingestionPipeline,
      sentence_window_node_parsing: {
        ...ingestionPipeline?.sentence_window_node_parsing,
        window_sentence_padding: toNumberOrUndefined(value),
      },
    } as IngestionPipelineOutput);
    setWindowSentencePaddingShowDefault(false);
  };

  const onSentenceMinLengthChange = (value?: number | string) => {
    onChange?.({
      ...ingestionPipeline,
      sentence_window_node_parsing: {
        ...ingestionPipeline?.sentence_window_node_parsing,
        sentence_min_length: toNumberOrUndefined(value),
      },
    } as IngestionPipelineOutput);
    setSentenceMinLengthShowDefault(false);
  };

  const onSentenceMaxLengthChange = (value?: number | string) => {
    onChange?.({
      ...ingestionPipeline,
      sentence_window_node_parsing: {
        ...ingestionPipeline?.sentence_window_node_parsing,
        sentence_max_length: toNumberOrUndefined(value),
      },
    } as IngestionPipelineOutput);
    setSentenceMaxLengthShowDefault(false);
  };

  const onLLMNodeParsingLLMChange = (llm: LLMSettings) => {
    onChange?.({
      ...ingestionPipeline,
      llm_node_parsing: {
        ...ingestionPipeline?.llm_node_parsing,
        llm,
      },
    } as IngestionPipelineOutput);
  };

  const onLLMNodeParsingPromptChange = (value: string) => {
    onChange?.({
      ...ingestionPipeline,
      llm_node_parsing: {
        ...ingestionPipeline?.llm_node_parsing,
        prompt: value,
      },
    } as IngestionPipelineOutput);
  };

  const onLLMNodeParsingChunkSizeChange = (value?: number | string) => {
    onChange?.({
      ...ingestionPipeline,
      llm_node_parsing: {
        ...ingestionPipeline?.llm_node_parsing,
        chunk_size: toNumberOrUndefined(value),
      },
    } as IngestionPipelineOutput);
    setLLMNodeParsingChunkSizeShowDefault(false);
  };

  const onLLMNodeParsingDocumentMaxSizeChange = (value?: number | string) => {
    onChange?.({
      ...ingestionPipeline,
      llm_node_parsing: {
        ...ingestionPipeline?.llm_node_parsing,
        document_max_size: toNumberOrUndefined(value),
      },
    } as IngestionPipelineOutput);
  };

  const renderEmbeddingBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Settings: Embedding model')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Settings: Embedding model: Model selection')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Settings: Embedding model: Model selection: Description')}
        </IbTypography.Paragraph>
        <ModelSelector.Embedding
          descriptor={
            getShowError(validationState.embedding) && (
              <IbTypography.Paragraph error type="descriptor">
                {getErrorMessage(validationState.embedding, t('Settings: Embedding model: Model selection: Error'))}
              </IbTypography.Paragraph>
            )
          }
          loading={providersLoading}
          providers={providers}
          settings={ingestionPipeline?.embedding}
          status={getShowError(validationState.embedding) ? 'error' : 'default'}
          onBlur={onEmbeddingBlur}
          onChange={onEmbeddingChange}
        />
      </div>
    </div>
  );

  const renderTextSplittingBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Settings: Method for splitting text into chunks')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">
          {t('Settings: Method for splitting text into chunks: Description')}
        </IbTypography.Paragraph>
      </div>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <Switcher
          options={[
            { value: NodeParsingMode.Chunk, label: t('Settings: Splitting: By chunk size') },
            { value: NodeParsingMode.SentenceWindow, label: t('Settings: Splitting: By sentences') },
            { value: NodeParsingMode.Llm, label: t('Settings: Splitting: By LLM') },
          ]}
          value={ingestionPipeline?.node_parsing_mode}
          onChange={onNodeParsingModeChange}
        />
      </div>
      {ingestionPipeline?.node_parsing_mode == NodeParsingMode.Chunk && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: Chunk size')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: Chunk size: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput min={MIN_CHUNK_SIZE} value={chunkSize ?? undefined} onChange={onChunkSizeChange} />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: Chunk overlap')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: Chunk overlap: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput min={MIN_CHUNK_OVERLAP} value={chunkOverlap ?? undefined} onChange={onChunkOverlapChange} />
          </div>
        </>
      )}
      {ingestionPipeline?.node_parsing_mode == NodeParsingMode.SentenceWindow && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: Sentence count')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: Sentence count: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              min={MIN_SENTENCE_COUNT}
              value={sentenceCount ?? undefined}
              onChange={onSentenceCountChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: Window overlay size')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: Window overlay size: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              min={MIN_WINDOW_SENTENCE_PADDING}
              value={windowSentencePadding ?? undefined}
              onChange={onWindowSentencePaddingChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: Sentence min size')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: Sentence min size: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              min={MIN_SENTENCE_MIN_LENGTH}
              value={sentenceMinLength ?? undefined}
              onChange={onSentenceMinLengthChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: Sentence max size')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: Sentence max size: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              min={MIN_SENTENCE_MAX_LENGTH}
              value={sentenceMaxLength ?? undefined}
              onChange={onSentenceMaxLengthChange}
            />
          </div>
        </>
      )}
      {ingestionPipeline?.node_parsing_mode == NodeParsingMode.Llm && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: LLM model')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: LLM model: Description')}
            </IbTypography.Paragraph>
            <ModelSelector.LLM
              loading={providersLoading}
              providers={providers}
              settings={ingestionPipeline?.llm_node_parsing?.llm}
              onChange={onLLMNodeParsingLLMChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: Chunking prompt')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: Chunking prompt: Description')}
            </IbTypography.Paragraph>
            <PromptEditorExpandable
              promptLanguage={PromptLanguage.SPLITTING}
              value={getDefaultIfUndefined(ingestionPipeline?.llm_node_parsing?.prompt, DEFAULT_LLM_SPLITTER_PROMPT)}
              onChange={onLLMNodeParsingPromptChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: Chunk size')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: Chunk size: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              min={MIN_CHUNK_SIZE}
              value={llmNodeParsingChunkSize ?? undefined}
              onChange={onLLMNodeParsingChunkSizeChange}
            />
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Settings: Splitting: Document max size')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Settings: Splitting: Document max size: Description')}
            </IbTypography.Paragraph>
            <IbNumberInput
              min={MIN_DOCUMENT_MAX_SIZE}
              value={ingestionPipeline.llm_node_parsing?.document_max_size ?? undefined}
              onChange={onLLMNodeParsingDocumentMaxSizeChange}
            />
          </div>
        </>
      )}
    </div>
  );

  const renderSaveButtonText = () => {
    return t('Settings: Save');
  };

  const renderRebuildButtonText = () => {
    if (ingestionState?.status == IngestionStateStatus.Processing) {
      return t('Settings: Progress', {
        value: getPercent(ingestionState.progress.current, ingestionState.progress.maximum),
      });
    }

    if (!ingestionState || ingestionState?.sources.length) {
      return t('Settings: Reindex');
    }

    return null;
  };

  const renderButtons = () => {
    const rebuildText = renderRebuildButtonText();

    if (!rebuildText) {
      return (
        <IbButton disabled={saving} onClick={onSaveButtonClick}>
          {renderSaveButtonText()}
        </IbButton>
      );
    }

    const actions: IIbButtonAction[] = [
      {
        icon: <IbIcon iconName="afferent-three" />,
        text: renderSaveButtonText(),
        disabled: saving,
        onSelect: onSaveButtonClick,
      },
    ];

    return (
      <IbButton actions={actions} disabled={rebuildDisabled} onClick={onRebuildButtonClick}>
        {rebuildText}
      </IbButton>
    );
  };

  const sections = [
    { id: 'embeddings', title: t('Settings: Embedding model'), content: renderEmbeddingBlock() },
    {
      id: 'text-splitting',
      title: t('Settings: Method for splitting text into chunks'),
      content: renderTextSplittingBlock(),
    },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={CONTENT_CLASS_NAME}>
        <IbScrollSpy sections={sections} />
      </div>
      <div className={FOOTER_CLASS_NAME}>{renderButtons()}</div>
    </div>
  );
};

export default ProjectIngestionPipeline;
